import React, {
  useState,
  useCallback,
  useContext,
  useRef,
} from 'react';
import { useMutation } from '@apollo/client';
import { BubbleRecommendations } from '@xp-utilities/web';
import ShownIn from '../../components/Swatches/ShownIn';
import SwatchGroup from '../../components/Swatches/SwatchGroup';
import SizeTileSection from '../../components/SizeTileSection/SizeTileSection';
import ProductNameAndDescription from '../../components/ProductNameAndDescription/ProductNameAndDescription';
import ProductReviews from '../../features/ProductReviews/ProductReviews';
import BuyGridAccordionWrapper from '../../features/BuyGridAccordions/BuyGridAccordionWrapper';
import SizeMessaging from '../../components/SizeMessaging/SizeMessaging';
import Quantity from '../../components/Quantity/Quantity';
import AddToBagButton from '../../components/AddToBagButton/AddToBagButton';
import ProductPriceSection from '../../components/ProductPrice/ProductPrice';
import ProductPageContext from '../../contexts/ProductPageContext';
import BreakpointContext from '../../contexts/BreakpointContext';
import MemberEarlyAccessButton from '../../components/MemberEarlyAccessButton/MemberEarlyAccessButton';
import StickyAddToBagManager from '../../components/StickyAddToBag/StickyAddToBagManager';
import BrowserContext from '../../contexts/BrowserContext';
import HorizontalLineSeparator from '../../components/HorizontalSeparator/HorizontalSeparator';
import FinalSale from '../../components/FinalSale/FinalSale';
import OmniWidget from '../../components/OmniWidget/OmniWidget';
import CrsOmniWidget from '../../components/OmniWidget/CrsOmniWidget';
import ListAndShareSection from '../../features/ListAndShareSection/ListAndShareSection';
import ESpotContent from '../../components/ESpotContent/ESpotContent';
import ContextualFit from '../../components/ContextualFit/ContextualFit';
import useUserState from '../../tools/UserState/useUserState';
import PayOverTime from '../../components/PayOverTime/PayOverTime';
import CompareFabricsSection from '../../components/CompareFabrics/CompareFabricsSection/CompareFabricsSection';
import numericValue from '../../tools/numericValue';
import AnalyticsTriggers from '../../helpers/analyticsTriggers';
import SwitchTestContext from '../../contexts/SwitchTestContext';
import MeasurementsAndStyliticsOverlay from '../../features/Images/MeasurementsAndStyliticsOverlay';
import ElevatedFit from '../../features/ElevatedFit/ElevatedFit';
import useDenimFeatureTestHook from '../../tools/customHooks/useDenimFeatureTestHook';
import AddToBagInstrumentation from '../../components/Recommendations/AddToBagInstrumentation';
import { generateRecommendationsEventName } from '../../contexts/AddToBagInstrumentationsContext';
import useStickyAtbFeatureTest from '../../tools/customHooks/useStickyAtbFeatureTest';
import FullStickyAddToBag from '../../features/FullStickyAddToBag/FullStickyAddToBag';
import HighlightedTextWrapper from '../../components/HighlightedTextWrapper/HighlightedTextWrapper';
import useScrollBehavior from '../../tools/customHooks/useScrollBehavior';
import DesktopStickyAddToBag from '../../features/DesktopStickyAddToBag/DesktopStickyAddToBag';
import useSubmitAddToBag from '../../helpers/useAddToBagForm';
import useAddToBagErrorHandler from '../../helpers/useAddToBagFormErrorHandler';
import ADD_TO_BAG_MUTATION from '../../gql/atbMutations';
import ContentError from '../../components/ContentError/ContentError';
import useIsExternalUser from '../../tools/customHooks/useIsExternalUser';
import hasATBUrgencyMessage from '../../tools/urgencyMessageTool';

export default function AddToBagForm({
  swatchChanged,
  categoryId,
  handlePrimarySizeUpdate,
  handleSecondarySizeUpdate,
  handleStickyPrimarySizeUpdate,
  handleStickySecondarySizeUpdate,
  reviewData,
  shippingContent,
  setSizeGuideOpen,
  siteRegion,
  brand,
  hasPDPMFEBelowTheFold,
}) {
  const [addToBag] = useMutation(ADD_TO_BAG_MUTATION, {
    onCompleted: (data) => {
      handleMutationComplete(data);
    },
    onError: () => handleFormError(tmntData?.genericCartAPIError),
  });
  const buyGridRef = useRef(null);
  const inlineButtonRef = useRef(null);
  const ProductPageState = useContext(ProductPageContext);
  const isBrowser = useContext(BrowserContext);
  const breakpoints = useContext(BreakpointContext);
  const SwitchTestState = useContext(SwitchTestContext);

  const sizeTileSelectionRef = useRef(null);
  const swatchSelectionRef = useRef(null);

  const {
    tmntData, collectionData, store, storeId, content,
  } = ProductPageState;

  const {
    prices, fisEligibility, popinsEligibility,
  } = collectionData?.activeProduct || {};

  const [selectedQuantity, setSelectedQuantity] = useState('1');
  const [activeButtonLabel, setActiveButtonLabel] = useState(collectionData?.addToBagSplitTestKeys?.activeLabel);

  const isOmniEnabled = collectionData?.isOmniEnabled;
  const isNewMfeOmniEnabled = collectionData?.isNewMfeOmniEnabled;
  const hasDenimElevatedAttrFeatureTest = collectionData?.hasDenimElevatedAttrFeatureTest;
  const hasStickyAddToBagDesktopEnabled = collectionData?.hasStickyAddToBagDesktopEnabled;
  const addToBagSplitTestKeys = collectionData?.addToBagSplitTestKeys;

  const denimElevatedAndScalesFlag = SwitchTestState['denim-elevated-and-scales'];
  const stickyAtbDesktopExperienceTest = SwitchTestState['sticky-atb-desktop-experience'];
  const hasCategoryRecs = SwitchTestState['pdp-has-category-recs'];
  const atbLabelText = hasATBUrgencyMessage(collectionData?.showUrgencyMessage, activeButtonLabel);

  const { hasElevatedFitDescription } = useDenimFeatureTestHook(hasDenimElevatedAttrFeatureTest, denimElevatedAndScalesFlag);

  const {
    isStandaloneStickyAtbEnabled,
    isStickyAtbWidgetEnabled,
  } = useStickyAtbFeatureTest(hasStickyAddToBagDesktopEnabled, stickyAtbDesktopExperienceTest);

  const isExternalUser = useIsExternalUser(ProductPageState);
  const { isStickyVisible, isFullBleedVisible } = useScrollBehavior(buyGridRef, inlineButtonRef, isStandaloneStickyAtbEnabled);

  const { isLoggedIn, isGenericUser } = useUserState(storeId);

  const resetATBButton = useCallback(() => {
    setActiveButtonLabel(addToBagSplitTestKeys?.activeLabel);
  }, [addToBagSplitTestKeys?.activeLabel]);

  const { addToBagError, setAddToBagError, handleFormError } = useAddToBagErrorHandler(collectionData, tmntData, resetATBButton);

  const swatchHandler = useCallback((event) => {
    swatchChanged(event);
    setAddToBagError(null);
  }, [swatchChanged, setAddToBagError]);

  const primarySizeTileHandler = useCallback(({ target }) => {
    handlePrimarySizeUpdate(target.value);
    setAddToBagError(null);
  }, [handlePrimarySizeUpdate, setAddToBagError]);

  const secondarySizeTileHandler = useCallback(({ target }) => {
    handleSecondarySizeUpdate(target.value);
    setAddToBagError(null);
  }, [handleSecondarySizeUpdate, setAddToBagError]);

  const stickyPrimarySizeTileHandler = useCallback(({ target }) => {
    handleStickyPrimarySizeUpdate(target.value);
    setAddToBagError(null);
  }, [handleStickyPrimarySizeUpdate, setAddToBagError]);

  const stickySecondarySizeTileHandler = useCallback(({ target }) => {
    handleStickySecondarySizeUpdate(target.value);
    setAddToBagError(null);
  }, [handleStickySecondarySizeUpdate, setAddToBagError]);

  const { handleSubmitForm } = useSubmitAddToBag({
    onMutationSubmit: addToBag,
    collectionData,
    isGenericUser,
    store,
    handleFormError,
    setActiveButtonLabel,
    tmntData,
  });

  const instrumentAddToBag = () => {
    const { kicId = '' } = collectionData?.activeProduct;
    window?.digitalData?.trigger(generateRecommendationsEventName('addToBag-1'), { products: { kic: kicId }, cartItems: kicId });
  };

  const handleMutationComplete = (data) => {
    if (data) {
      const { addToBagRequest } = data;
      if (addToBagRequest.success) {
        setActiveButtonLabel(addToBagSplitTestKeys?.successLabel);
        setTimeout(() => {
          resetATBButton();
        }, 3000);
        // TODO: figure out what event to trigger to cart on successful add
        if (isBrowser && isLargeDesktop) {
          const miniBagEvent = new CustomEvent('miniBag-toaster:open', { detail: 'productAddedToCart' });
          window.dispatchEvent(miniBagEvent);
        }
        addToBagAnalyticsEvent(addToBagRequest.responseData);
        if (isBrowser && !isLargeDesktop) {
          const addToBagNotificationEvent = new CustomEvent('mfe:addToBag:displayMobileNotification', { detail: addToBagRequest.responseData });
          window.dispatchEvent(addToBagNotificationEvent);
        }

        instrumentAddToBag();
      } else {
        const mutationError = addToBagRequest.errors[0].message;
        handleFormError(mutationError);
      }
    } else {
      handleFormError(tmntData.genericCartAPIError);
    }
  };

  const addToBagAnalyticsEvent = (atbResponseData) => AnalyticsTriggers.addToBag(ProductPageState, { selectedQuantity, atbResponseData });

  const hasGatedAddToBagExperience = !(isBrowser && collectionData?.isLoyaltyRegion && collectionData?.activeProduct?.isEarlyAccess ? isLoggedIn() : true);

  if (!collectionData) {
    return null;
  }

  const isFinalSale = collectionData?.activeProduct?.isFinalSale;
  const isMobile = breakpoints.xxsmall && !breakpoints.medium;
  const isLargeDesktop = breakpoints.large;

  const purchaseAmount = numericValue(prices.member?.originalPrice || prices.list?.discountPrice || prices.list?.originalPrice);

  const renderPayOverTimeAndReviews = () => (
    <>
      <div className="pay-over-time-product">
        <PayOverTime purchaseAmount={parseFloat(purchaseAmount)} />
      </div>
      <ProductReviews dataBvShow="rating_summary" />
    </>
  );

  const displayBeforeAddToBagESpot = () => {
    if (content?.pdpESpots?.pdp_beforeaddtobag?.content) {
      return <ESpotContent eSpotData={content?.pdpESpots?.pdp_beforeaddtobag} />;
    }
    return <ESpotContent eSpotData={content?.legacyPDPESpots?.ProductPromoEMS} />;
  };

  const titleWrapper = ({ children }) => (
    <h2 className={`bubble-recommendations-title ${brand}`}>{children}</h2>
  );

  return (
    <div className="add-to-bag-form-wrapper" data-testid="add-to-bag-form-wrapper" ref={buyGridRef}>
      <div className="product-page-info-container-mfe">
        {isMobile && !collectionData?.hasMobileAltImageThumbnail && (
          <MeasurementsAndStyliticsOverlay
            tmntData={tmntData}
            brand={brand}
            activeProduct={ProductPageState.collectionData.activeProduct}
            isMobile={isMobile}
            hasPDPMFEBelowTheFold={hasPDPMFEBelowTheFold}
          />
        )}
        <div className="desktop-view" data-testid="desktop-name-price-reviews">
          {hasCategoryRecs && isExternalUser && (
            <BubbleRecommendations className="bubble-recommendations-container" titleWrapper={titleWrapper} />
          )}
          <div data-testid="desktop-name-and-description-wrapper">
            <ProductNameAndDescription />
          </div>
          <ProductPriceSection />
          {isBrowser && !isMobile ? renderPayOverTimeAndReviews() : ''}
        </div>
        <div className="desktop-view">
          <HorizontalLineSeparator />
        </div>
        <div ref={swatchSelectionRef}>
          <ShownIn />
          <SwatchGroup
            attrChangeHandler={swatchHandler}
            siteRegion={siteRegion}
          />
        </div>
        <div className="mobile-view" data-testid="mobile-price-reviews">
          <ProductPriceSection />
          {isBrowser && isMobile ? renderPayOverTimeAndReviews() : ''}
          <HorizontalLineSeparator />
        </div>
        {hasElevatedFitDescription && (
          <ElevatedFit collectionId={ProductPageState.collectionId} />
        )}
        <div ref={sizeTileSelectionRef}>
          <SizeTileSection
            primarySizeTileHandler={primarySizeTileHandler}
            secondarySizeTileHandler={secondarySizeTileHandler}
            setSizeGuideOpen={setSizeGuideOpen}
          />
        </div>
        <HighlightedTextWrapper>
          <ContextualFit
            tmntData={tmntData}
            reviewData={reviewData}
            showIcon
          />
        </HighlightedTextWrapper>
        <SizeMessaging />
        {collectionData?.hasCompareOurFabricsEnabled && (
          <CompareFabricsSection
            header={tmntData.pdpDenimDetails?.value}
            label={tmntData.pdpCompareFabricLink?.value}
            brand={brand}
          />
        )}
        <ContentError error={addToBagError} />
        <div className="catalog-shop-similar__container" />
        {isFinalSale && (
          <FinalSale
            finalSaleMessage={tmntData?.finalSaleText?.value}
          />
        )}
        {hasGatedAddToBagExperience
          ? <MemberEarlyAccessButton /> : (
            <div className="add-to-bag-form-wrapper_button-section-mfe">
              {displayBeforeAddToBagESpot()}
              <div className="add-to-bag-from-wrapper_action-wrapper" data-testid="add-to-bag-button" ref={inlineButtonRef}>
                <Quantity
                  selectedQuantity={selectedQuantity}
                  handleChange={setSelectedQuantity}
                />
                <AddToBagButton
                  brand={collectionData.activeProduct.brand}
                  label={tmntData?.[atbLabelText]?.value}
                  handleAddToBag={() => handleSubmitForm(selectedQuantity)}
                  activeButtonLabel={activeButtonLabel}
                />
              </div>
            </div>
          )}
        <ListAndShareSection
          productId={collectionData.activeProduct.productId}
          kic={ProductPageState.collectionData?.activeProduct?.kicId}
          sku={ProductPageState?.collectionData.qualifiedSku}
          seq={ProductPageState.collectionData.activeProduct?.defaultSwatchSequence}
          store={store}
          storeId={storeId}
        />
        <ESpotContent eSpotData={content?.pdpESpots?.pdp_aftersaveforlater} />
        <BuyGridAccordionWrapper
          categoryId={categoryId}
          reviewData={reviewData}
          shippingContent={shippingContent}
          siteRegion={siteRegion}
        />
        <div className="mobile-view">
          <StickyAddToBagManager
            handleAddToBag={() => handleSubmitForm(selectedQuantity)}
            activeButtonLabel={activeButtonLabel}
            primarySizeTileHandler={stickyPrimarySizeTileHandler}
            secondarySizeTileHandler={stickySecondarySizeTileHandler}
            setSizeGuideOpen={setSizeGuideOpen}
            hasGatedAddToBagExperience={hasGatedAddToBagExperience}
            atbLabelText={atbLabelText}
          />
        </div>
        {((isOmniEnabled && !isNewMfeOmniEnabled) && (fisEligibility || popinsEligibility) && isBrowser) && <CrsOmniWidget />}
        {(isOmniEnabled && isNewMfeOmniEnabled) && <OmniWidget />}
        {!isMobile && isStandaloneStickyAtbEnabled && (
          <div className={`sticky-add-to-bag-form ${isStickyVisible ? 'isVisible' : ''}`}>
            <DesktopStickyAddToBag
              onAtbClick={() => handleSubmitForm(selectedQuantity)}
              activeButtonLabel={activeButtonLabel}
              brand={collectionData.activeProduct.brand}
              sizeTileSelectionRef={sizeTileSelectionRef}
              label={tmntData?.[atbLabelText]?.value}
              addToBagError={addToBagError}
            />
          </div>
        )}
        {!isMobile && isStickyAtbWidgetEnabled && (
          <FullStickyAddToBag
            brand={brand}
            productName={collectionData?.activeProduct.productName}
            productImage={collectionData?.activeProduct?.imageSet?.prod?.[0]?.thumbnailSrc}
            price={collectionData?.activeProduct?.prices?.list}
            swatchImage={collectionData?.activeProduct?.swatchImageUrl}
            productId={collectionData?.activeProduct?.productId}
            sizeLabel={collectionData?.qualifiedFullSizeLabel || tmntData?.selectSizeText?.value}
            addToBagLabel={tmntData?.[atbLabelText]?.value}
            activeButtonLabel={activeButtonLabel}
            handleAddToBag={() => handleSubmitForm(selectedQuantity)}
            sizeTileSelectionRef={sizeTileSelectionRef}
            swatchSelectionRef={swatchSelectionRef}
            addToBagError={addToBagError}
            isFullBleedVisible={isFullBleedVisible}
          />
        )}
      </div>
      <AddToBagInstrumentation />
    </div>
  );
}
